import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQueries,
  useQuery,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { notFound } from '@tanstack/react-router';
import { toast } from 'sonner';

import { isNotFoundError } from '@/infra/exceptions/helpers/isNotFoundError';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      retryOnMount: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      retry: false,
      onSuccess: (data) => {
        function validateMessage(data: unknown): data is { message: string } {
          return (
            typeof data === 'object' &&
            data !== null &&
            'message' in data &&
            typeof data.message === 'string' &&
            data.message.trim().length > 0
          );
        }

        const isValidMessage = validateMessage(data);

        if (isValidMessage) {
          toast.success(data.message);
        }
      },
    },
  },
});

const QueryTimers = {
  ONE_MINUTE: 1 * 60 * 1000,
  FIVE_MINUTES: 5 * 60 * 1000,
  ONE_HOUR: 1 * 60 * 60 * 1000,
} as const;

async function ensureNotFound<T>(promise: Promise<T>): Promise<T> {
  try {
    return await promise;
  } catch (error) {
    if (isNotFoundError(error)) {
      throw notFound();
    }
    throw error;
  }
}

export {
  queryClient,
  QueryClientProvider,
  ReactQueryDevtools,
  useMutation,
  useQueries,
  useQuery,
  QueryTimers,
  ensureNotFound,
};
